import {
  IpfError,
  isIpfError,
} from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { refreshTokens as refreshTokensCommand } from 'components/GlobalState/commands/refreshTokens';
import { reportSuspectedOutage } from 'components/GlobalState/commands/reportSuspectedOutage';
import { unsafeGlobalStateDispatch } from 'components/GlobalState/globalStateDispatch';
import {
  type AbortableApiRequestParams,
  type ApiRequestParams,
  isAbortableApiRequestParams,
} from './ApiRequestParams';
import { prolongingTokenEndpoints } from './requestProlongingToken';
import { isGatewayErrorStatus, isHtmlContentType } from './systemUnavailable';

// API_URL is processed during the frontend build
declare const API_URL: string;

const REQUEST_RATE_LIMIT_DELAY_MS = 500;

type RequestParams<T> = AbortableApiRequestParams<T> | ApiRequestParams<T>;

/**
 * Parses the JSON returned by a network request
 * response type text/plain: string is returned
 * response type application/json: object (parsed json) is returned
 * response type application/x-tar: archive is returned (.tar, etc)
 * response type application/octet-stream: archive is returned (.arc, .ark, etc)
 * response type text/markdown: string is returned
 */
const parseResponse = async (
  response: Response,
  path: string,
): Promise<unknown> => {
  const contentType = response.headers.get('Content-Type');
  if (!contentType) {
    return null;
  }

  if (contentType?.includes('text/plain')) {
    return response.text();
  }
  if (contentType?.includes('application/json')) {
    try {
      const json = await response.json();
      return json;
    } catch (e) {
      throw new IpfError(
        'GENERIC',
        `${path} can't parse JSON output. Error: ${e}`,
      );
    }
  }
  // Download files
  if (
    contentType.includes('application/x-tar') ||
    contentType.includes('application/octet-stream')
  ) {
    return response;
  }
  if (contentType.includes('text/markdown')) {
    return response.text();
  }
  // API never returns HTML. HTML is served in place of API responses e.g. by the system upgrade page
  // (200 + HTML). Returning the raw response would crash the userland code expecting parsed data.
  if (isHtmlContentType(contentType)) {
    await unsafeGlobalStateDispatch(reportSuspectedOutage);
    throw new IpfError(
      'GENERIC',
      `${path} returned an unexpected HTML response. Please wait a moment and reload the page.`,
    );
  }
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response;
};

/**
 * Checks if a network request came back fine, and throws an error if not
 */
const checkStatus = async (
  response: Response,
  path: string,
  options: RequestInit,
): Promise<Response> => {
  if (
    (response.status >= 200 && response.status < 300) ||
    response.status === 304
  ) {
    return Promise.resolve(response);
  }

  const contentType = response.headers.get('Content-Type');
  // JSON gateway errors come from the API server itself (e.g. extensions orchestrator unavailable)
  if (
    isHtmlContentType(contentType) ||
    (isGatewayErrorStatus(response.status) && !contentType?.includes('json'))
  ) {
    await unsafeGlobalStateDispatch(reportSuspectedOutage);
  }
  if (contentType?.includes('json')) {
    const json = await response.json();

    if (response.status === 403 && json?.code === 'API_INSUFFICIENT_RIGHTS') {
      throw new IpfError(
        'INSUFFICIENT_RIGHTS',
        `You don't have access to the following resource "${options.method} ${path}", contact your administrator.`,
      );
    }

    throw new IpfError('API', '', {
      response: json,
      url: path,
      httpCode: response.status,
    });
  }
  throw new IpfError(
    'API',
    `A request ${path} failed to fetch, HTTP status: ${response.status} Please wait a moment and try again.`,
    {
      response: { serverError: true },
      url: path,
      httpCode: response.status,
    },
  );
};

/**
 * Requests a URL, returning a promise
 */
const fetchData = async (
  path: string,
  options: RequestInit,
): Promise<unknown> => {
  const response = await fetch(`${API_URL}${path}`, options);
  const checkedResponse = await checkStatus(response, path, options);
  const parsedResponse = await parseResponse(checkedResponse, path);
  return parsedResponse;
};

export const request = async <TResult = any, TRequestData = any>(
  params: RequestParams<TRequestData>,
  refreshTokens: () => Awaitable<void>,
  navigate: (url: string) => void,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
): Promise<TResult> => {
  const { url, method, data, options, formData } = params;
  const appliedMethod = method?.toUpperCase() ?? 'GET';
  const defaultOptions: RequestInit & { headers: Record<string, string> } = {
    method: appliedMethod,
    headers: {},
    ...(isAbortableApiRequestParams(params)
      ? { signal: params.abortSignal }
      : {}),
  };

  if (appliedMethod !== 'GET') {
    if (formData) {
      if (!(data instanceof FormData)) {
        stumble('formData is not instance of FormData');
      } else {
        // content type is populated automatically for form data by the fetch function
        defaultOptions.body = data as FormData;
      }
    } else if (data) {
      defaultOptions.headers['Content-Type'] = 'application/json';
      defaultOptions.body = JSON.stringify(data);
    } else {
      defaultOptions.body = null;
    }
  }
  const requestHeaders: HeadersInit = {
    ...defaultOptions.headers,
    ...(options?.headers ?? {}),
  };
  const requestOptions: RequestInit = {
    ...defaultOptions,
    ...options,
    headers: requestHeaders,
  };

  do {
    try {
      const response = await fetchData(
        url.startsWith('/') ? url : `/${url}`,
        requestOptions,
      );
      return response as TResult;
    } catch (e) {
      if (!isIpfError(e)) {
        throw e;
      }

      const errCode = e?.code;
      if (errCode === 'API_INVALID_CREDENTIALS') {
        throw new IpfError('INVALID_CREDENTIALS', e?.message);
      }
      // Endpoint that refreshes tokens should not trigger token refresh loop
      if (
        errCode === 'API_EXPIRED_ACCESS_TOKEN' &&
        !prolongingTokenEndpoints.includes(url)
      ) {
        await refreshTokens();
        // retry
      } else if (
        errCode === 'API_INVALID_REFRESH_TOKEN' ||
        errCode === 'API_INVALID_ACCESS_TOKEN'
      ) {
        if (errCode === 'API_INVALID_ACCESS_TOKEN') {
          navigate('/logout');
        }
        throw e;
      } else if (isIpfError(e, 'API') && e.payload?.httpCode === 429) {
        await sleep(REQUEST_RATE_LIMIT_DELAY_MS);
        // retry
      } else {
        throw e;
      }
    }
    // All exit branches contain return or throw statement, passing to this point = retry.
    // Using this rather than recursion to avoid deep call stacks.
    // biome-ignore lint/correctness/noConstantCondition: intentional
  } while (true);
};

export const requestForSaga = async <TResult = any, TRequestData = any>(
  params: RequestParams<TRequestData>,
): Promise<TResult> => {
  return request(
    params,
    () => unsafeGlobalStateDispatch(refreshTokensCommand),
    (url) => {
      window.location.href = url;
    },
  );
};
